import { SupplierFormFieldKey, SupplierFormValues } from 'src/types/manage-suppliers/SupplierForm';

export const SUPPLIER_FIELD_MAX_LENGTHS = {
  supplierName: 100,
  externalSupplierName: 100,
  apId: 30,
  accountNumber: 30,
  address1: 75,
  address2: 75,
  city: 75,
  stateProvince: 35,
  zipPostalCode: 15,
  country: 100,
  contactName: 100,
  phone: 35,
  orderEmail: 75,
} as const satisfies Partial<Record<SupplierFormFieldKey, number>>;

export type SupplierLengthValidatableField = keyof typeof SUPPLIER_FIELD_MAX_LENGTHS;

export const isSupplierLengthValidatableField = (
  field: SupplierFormFieldKey
): field is SupplierLengthValidatableField => field in SUPPLIER_FIELD_MAX_LENGTHS;

export const isSupplierFieldOverMaxLength = (
  field: SupplierLengthValidatableField,
  value: string
): boolean => value.length > SUPPLIER_FIELD_MAX_LENGTHS[field];

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

export const validateSupplierFieldMaxLength = (
  value: string,
  maxLength: number,
  t: TranslateFn
): string | undefined => {
  if (value.length > maxLength) {
    return t('Character limit of {{max}} exceeded.', { max: maxLength });
  }

  return undefined;
};

export const getSupplierFormLengthErrors = (
  values: Pick<SupplierFormValues, SupplierLengthValidatableField>,
  fieldAccess: Partial<Record<SupplierFormFieldKey, boolean>>,
  t: TranslateFn
): Partial<Record<SupplierLengthValidatableField, string>> => {
  const errors: Partial<Record<SupplierLengthValidatableField, string>> = {};

  (Object.keys(SUPPLIER_FIELD_MAX_LENGTHS) as SupplierLengthValidatableField[]).forEach((field) => {
    if (fieldAccess[field] === false && !values[field].trim()) {
      return;
    }

    const maxLength = SUPPLIER_FIELD_MAX_LENGTHS[field];
    const error = validateSupplierFieldMaxLength(values[field], maxLength, t);

    if (error) {
      errors[field] = error;
    }
  });

  return errors;
};
